import { memo, type PropsWithChildren, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useTrackVersionUploadInitiated } from '@air/analytics';
import { Clips } from '@air/api';
import { type Clip } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type DuplicateItem, DuplicateItemModal } from '~/components/Modals/DuplicateItemModal';
import { PreUploadModal } from '~/components/Modals/PreUploadModal';
import { UploadVersionsModal } from '~/components/Modals/UploadVersionsModal';
import { type OnUploadParams, UploadDropzone } from '~/components/PrivateDropzone/components/UploadDropzone';
import { EditCanvasDropzoneProvider } from '~/components/PrivateDropzone/EditCanvasDropzoneContext';
import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { useShowFirstUploadPromptValue } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { usePrivateSmartSearchSimilarToFilter } from '~/hooks/filters/usePrivateSmartSearchSimilarToFilter';
import {
  splitFilesByFolder,
  type TopLevelFileUploadDecision,
  useQueuePrivateUploads,
} from '~/hooks/useQueuePrivateUploads';
import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useUploadValidation } from '~/hooks/useUploadValidation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { PrivateUploadUrlsManagerProvider } from '~/providers/PrivateUploadUrlsManagerProvider';
import {
  centralizedBoardAncestorsSelector,
  centralizedBoardLibrarySelector,
  centralizedBoardTitleSelector,
} from '~/store/centralizedBoard/selectors';
import { centralizedClipAssetIdSelector, centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { useCanUploadAssets } from '~/swr-hooks/members/useCanUploadAssets';
import { type BoardsSelectOption } from '~/types/BoardSearch';
import { ACCEPTED_MIME_TYPES, resizeImageToBase64 } from '~/utils/imageSearch';
import { getBoardIdFromPath, getClipIdFromPath } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export type PrivateDropzoneProps = PropsWithChildren<object>;

export const PrivateDropzone = memo(({ children }: PrivateDropzoneProps) => {
  const store = useAirStore();
  const { showToast } = useToasts();
  const currentAssetId = useSelector(centralizedClipAssetIdSelector);
  const { canUploadAssets } = useCanUploadAssets();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const [showDuplicateItemModal] = useAirModal(DuplicateItemModal);
  const [showPreUploadModal] = useAirModal(PreUploadModal);
  const [showUploadVersionsModal, closeUploadVersionsModal] = useAirModal(UploadVersionsModal);
  const { trackVersionUploadInitiated } = useTrackVersionUploadInitiated();
  const { similarToImageSearchAvailable } = usePrivateSearchUIHelpers();
  const { addSimilarToImageFilter } = usePrivateSmartSearchSimilarToFilter();
  const { validateUpload } = useUploadValidation({ workspaceId });
  const { isStoragePlanRestricted, showStoragePlanRestrictionModal } = useStoragePlanRestriction();
  const { queuePrivateUploads } = useQueuePrivateUploads();

  const clipId = useSelector(centralizedClipIdSelector);
  const isAssetModalOpen = !!clipId;

  const { isSearchCommandOpen } = usePrivateSearchContext();

  const showFirstUploadPrompt = useShowFirstUploadPromptValue(workspaceId);

  const dropzoneDisabled =
    showFirstUploadPrompt ||
    !canUploadAssets ||
    (isSearchCommandOpen && similarToImageSearchAvailable) ||
    isAssetModalOpen;

  const currentBoardId = typeof window !== 'undefined' ? getBoardIdFromPath(window.location.pathname) : null;
  const currentBoardTitle = centralizedBoardTitleSelector(store.getState());
  const currentBoardLibrary = centralizedBoardLibrarySelector(store.getState());

  const onUpload = useCallback(
    async ({ acceptedFiles, rejectedFiles, dropZone }: OnUploadParams) => {
      if (!workspaceId) {
        return;
      }

      if (isStoragePlanRestricted) {
        showStoragePlanRestrictionModal();
        return;
      }

      // Validate files: check permissions, limits, file extensions, and restricted characters
      const validatedFiles = await validateUpload(acceptedFiles);

      if (!validatedFiles) {
        return;
      }

      const { files, emptyFiles, filesWithNoExtension } = validatedFiles;

      let shouldCancelUpload = false;
      let selectedBoard: null | BoardsSelectOption = null;
      /**
       * `isModalVersion` is a version upload from the `VersionsUploadModal` from the AssetModal
       * as opposed to an auto-versioning prompt based on a duplicate item name or title
       */
      let isModalVersion = false;
      let assetId: Clip['assetId'] | undefined;

      const clipIdInUrl = getClipIdFromPath(window.location.pathname);

      // @deprecated This version upload logic is only used by the V1 asset modal.
      // V2 asset modal uses PrivateAssetModalDropzone instead.
      // Once V1 is fully removed, this entire clipIdInUrl section can be deleted.
      // Only add assetId to make upload a version if uploading to an asset
      if (clipIdInUrl) {
        isModalVersion = await new Promise<boolean>((accept) =>
          showUploadVersionsModal({
            onUpload: () => accept(true),
            onCancel: () => {
              shouldCancelUpload = true;
              accept(false);
            },
            files,
          }),
        );

        closeUploadVersionsModal();

        if (isModalVersion) {
          assetId = currentAssetId;
          trackVersionUploadInitiated();
        }
      } else if (dropZone === 'upload' && currentBoardId) {
        // Right zone: Direct upload to current board (bypass modal)
        const currentBoardAncestors = centralizedBoardAncestorsSelector(store.getState());

        if (currentBoardTitle && currentBoardAncestors) {
          selectedBoard = {
            id: currentBoardId,
            library: currentBoardLibrary,
            title: currentBoardTitle,
            ancestors: currentBoardAncestors,
          };
        }
      } else if (dropZone === 'search-by-image' && similarToImageSearchAvailable) {
        try {
          if (acceptedFiles.length !== 1) {
            showToast('Please upload a single image to search by', { color: 'red' });
            return;
          }

          const file = acceptedFiles[0];

          if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
            showToast('Please upload a JPEG or PNG image to search by', { color: 'red' });
            return;
          }

          const base64Image = await resizeImageToBase64(file);
          addSimilarToImageFilter(base64Image, 'drag-and-drop');
        } catch (error) {
          showToast('Failed to process image. Please try again.', { color: 'red' });
          reportErrorToBugsnag({
            error,
            context: 'PrivateDropzone: Failed to process image',
          });
        }

        return;
      } else {
        // Left zone or no specific zone: Show PreUploadModal
        selectedBoard = await new Promise<BoardsSelectOption | null>((accept) => {
          // This will allow the modal to show deeply nested boards in
          // tree view.
          const currentBoardAncestors = centralizedBoardAncestorsSelector(store.getState());

          showPreUploadModal({
            onUpload: accept,
            onCancel: () => {
              shouldCancelUpload = true;
              accept(null);
            },
            initialBoard:
              currentBoardId && currentBoardTitle && currentBoardAncestors
                ? {
                    id: currentBoardId,
                    library: currentBoardLibrary,
                    title: currentBoardTitle,
                    ancestors: currentBoardAncestors,
                  }
                : undefined,
          });
        });
      }

      if (shouldCancelUpload) {
        return;
      }

      const [filesInFolders, filesNotInFolders] = splitFilesByFolder(files);
      let duplicateHandlingData: DuplicateItem[] = [];

      if (filesNotInFolders.length) {
        /**
         * This endpoint takes the files that the user is trying to upload and checks
         * if any of the other assets in that board have the same filename.
         */
        const { data: matchedFilesToClips } = await Clips.matchFilesToClips({
          workspaceId,
          files: filesNotInFolders.map(({ name }) => ({ name })),
          boardId: selectedBoard?.id,
        });

        /**
         * We store `index` because `Clips.matchFilesToClips` returns an array in the original order, matching `files` to `Clips` uploaded
         * `matchedFilesToClips` matched them by file.name
         */
        const duplicateFiles = matchedFilesToClips
          .map((d, index) => ({ ...d, index }))
          .filter((item) => !!item.assetId);

        if (duplicateFiles.length > 0 && selectedBoard && !isModalVersion) {
          duplicateHandlingData = await new Promise((accept) => {
            showDuplicateItemModal({
              items: duplicateFiles,
              onComplete: accept,
              onCancel: () => {
                shouldCancelUpload = true;
                accept([]);
              },
            });
          });
        }
      }

      if (shouldCancelUpload) {
        return;
      }

      const boardToUpload = selectedBoard
        ? selectedBoard
        : currentBoardId && currentBoardTitle
          ? { title: currentBoardTitle, id: currentBoardId }
          : undefined;

      const duplicateHandlingDataDict = duplicateHandlingData.reduce<Record<number, DuplicateItem>>((acc, item) => {
        acc[item.index] = item;
        return acc;
      }, {});

      const topLevelFilesToUpload = filesNotInFolders.reduce((acc: TopLevelFileUploadDecision[], file, index) => {
        const duplicateDecision = duplicateHandlingDataDict[index];

        if (duplicateDecision?.choice === 'cancel') {
          return acc;
        }

        const assetIdDestination = isModalVersion
          ? assetId
          : duplicateDecision?.choice === 'stack'
            ? duplicateDecision.assetId
            : undefined;

        acc.push({
          file,
          assetId: assetIdDestination,
        });

        return acc;
      }, []);

      queuePrivateUploads({
        filesInFolders,
        topLevelFilesToUpload,
        boardToUpload,
        rejectedFiles,
        emptyFiles,
        filesWithNoExtension,
      });
    },
    [
      workspaceId,
      validateUpload,
      store,
      currentAssetId,
      showUploadVersionsModal,
      closeUploadVersionsModal,
      trackVersionUploadInitiated,
      currentBoardId,
      currentBoardTitle,
      currentBoardLibrary,
      showPreUploadModal,
      showDuplicateItemModal,
      similarToImageSearchAvailable,
      addSimilarToImageFilter,
      showToast,
      queuePrivateUploads,
      isStoragePlanRestricted,
      showStoragePlanRestrictionModal,
    ],
  );

  return (
    <PrivateUploadUrlsManagerProvider>
      <EditCanvasDropzoneProvider>
        <UploadDropzone
          onUpload={onUpload}
          disabled={dropzoneDisabled}
          supportsDirectUpload={!!currentBoardId}
          supportsSimilarToImageSearch={similarToImageSearchAvailable}
          currentBoardTitle={currentBoardTitle}
          currentWorkspaceName={currentWorkspace?.name}
        >
          {children}
        </UploadDropzone>
      </EditCanvasDropzoneProvider>
    </PrivateUploadUrlsManagerProvider>
  );
});

PrivateDropzone.displayName = 'PrivateDropzone';
